import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/AuthService';
import { log } from '../../utils/errors/logger';
import type { AuthUser } from '../../types/common';
import { getRedirectPath } from '../../utils/security/redirectValidation';

interface UseAuthReturn {
  authenticated: boolean;
  checking: boolean;
  user: AuthUser | null;
  sessionExpiredOpen: boolean;
  handleLogin: (user: AuthUser) => void;  // ✅ CHANGED: Removed token parameter (cookies handle auth)
  handleLogout: () => void;
  handleSessionExpiredClose: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const navigate = useNavigate();

  // SECURITY: Get organization-aware redirect path for login
  // Uses whitelist validation to prevent open redirect attacks
  // OWASP: A01:2021 - Broken Access Control
  const getLoginRedirectPath = (): string => {
    // Use secure redirect path validation
    // This function:
    // 1. Sanitizes organization slug from localStorage
    // 2. Validates path against whitelist
    // 3. Falls back to /login if validation fails
    return getRedirectPath();
  };

  // Handler for session expired dialog close
  const handleSessionExpiredClose = async (): Promise<void> => {
    setSessionExpiredOpen(false);
    const redirectPath = getLoginRedirectPath();
    await AuthService.logout();
    setAuthenticated(false);
    navigate(redirectPath);
  };

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async (): Promise<void> => {
      const isAuthenticated = await AuthService.checkAuthentication();
      setAuthenticated(isAuthenticated);
      setChecking(false);
    };

    // Add listener for unauthorized events (token expiration)
    const handleUnauthorized = (): void => {
      log.info('Unauthorized event received, showing timeout dialog');
      setAuthenticated(false);
      setSessionExpiredOpen(true);
    };

    // Add listener for auth state changes (successful login)
    const handleAuthStateChanged = (): void => {
      log.info('🔔 Auth state changed event received');
      // ✅ FIXED: Don't call checkAuthentication() here to prevent infinite loop
      // The event is dispatched by setUser(), which means user data is already cached
      // We just need to update the authenticated state based on cached user
      const user = AuthService.getUser();
      const isAuthenticated = user !== null;
      log.info('🔍 Authentication state from cached user:', { isAuthenticated, username: user?.username });
      setAuthenticated(isAuthenticated);
      log.info('📱 App authentication state updated to:', { isAuthenticated });
    };

    document.addEventListener('unauthorized', handleUnauthorized);
    window.addEventListener('authStateChanged', handleAuthStateChanged);
    void checkAuth();

    // Clean up event listeners
    return () => {
      document.removeEventListener('unauthorized', handleUnauthorized);
      window.removeEventListener('authStateChanged', handleAuthStateChanged);
    };
  }, []);

  // ✅ CHANGED: handleLogin no longer needs token parameter (cookies handle auth)
  // User data is already cached by AuthService.login(), no need to call setUser again
  const handleLogin = (user: AuthUser): void => {
    // Don't call AuthService.setUser(user) - it's already set by AuthService.login()
    // Calling it again would trigger authStateChanged event unnecessarily
    setAuthenticated(true);
    navigate('/dashboard');
  };

  const handleLogout = async (): Promise<void> => {
    const redirectPath = getLoginRedirectPath();
    await AuthService.logout();
    setAuthenticated(false);
    navigate(redirectPath);
  };

  const handleLogoutWrapper = (): void => {
    void handleLogout();
  };

  const handleSessionExpiredCloseWrapper = (): void => {
    void handleSessionExpiredClose();
  };

  return {
    authenticated,
    checking,
    user: AuthService.getUser(),
    sessionExpiredOpen,
    handleLogin,
    handleLogout: handleLogoutWrapper,
    handleSessionExpiredClose: handleSessionExpiredCloseWrapper,
  };
};
